import { useContext, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch } from 'react-redux';

import { FontSizes, FontWeights, Text } from '@calm-web/design-system';

import { MediaPlayerContext } from '@/components/app/contentPlayer/MediaPlayerProvider';
import AuthForm from '@/components/authForm/AuthForm';
import Header from '@/components/header/Header';
import Image from '@/components/image/NextImage';
import { useAnalytics } from '@/hooks/analytics';
import {
	useAuthFormModeState,
	useGuideState,
	useProgramState,
	useUserState,
} from '@/hooks/store/useAppState';
import genericMessages from '@/messages/messages';
import { setUser } from '@/store/user/actions';
import { User } from '@/store/user/types';
import { userCanTrial } from '@/utils/subscriptions';

import ContentDisplay from '../ContentDisplay';
import { ShareContext } from '../ShareProvider';
import messages from './messages';
import {
	AuthContainer,
	AuthHeading,
	Container,
	CTAButton,
	Description,
	HeaderText,
	HeaderWrapper,
	Wrapper,
} from './styles';

const ASPECT_RATIO = 216 / 84;

export interface PrimerProps {
	isPreviewMode?: boolean;
}

function SharePrimer({ isPreviewMode }: PrimerProps): JSX.Element {
	const authFormMode = useAuthFormModeState();
	const guide = useGuideState();
	const program = useProgramState();
	const user = useUserState();
	const dispatch = useDispatch();
	const [activeScreen, setActiveScreen] = useState('primer');
	const { formatMessage } = useIntl();
	const { onPlay, playerMode, onPause } = useContext(MediaPlayerContext);
	const { isShareContentRecipient, shareSenderFirstName, shareToken, shouldShowPrimer, setPrimerVisible } =
		useContext(ShareContext);

	const { logEvent } = useAnalytics();

	function CTAAction() {
		if (!user) {
			setActiveScreen('auth');
		} else {
			setPrimerVisible(false);
			onPlay();
		}
	}

	function onAuthSuccess(newUser: User) {
		dispatch(setUser(newUser));
		setPrimerVisible(false);
		onPlay();
	}

	useEffect(() => {
		if (shouldShowPrimer && isShareContentRecipient) {
			logEvent({
				eventName: 'Screen : Viewed',
				eventProps: {
					screen: 'Signup Primer Screen',
					share_token: shareToken as string,
					sender_first_name: shareSenderFirstName || null,
					author_name: program?.author?.name || null,
					program_title: program?.title || null,
					program_id: program?.id || null,
					meditation_type: program?.meditation_type || null,
					narrator_name: program?.narrator?.name || null,
					program_language: program?.language || null,
					program_is_free: guide?.is_free || false,
					narrator_id: program?.narrator?.id || null,
				},
			});
		}
	}, [shareSenderFirstName, shareToken, program, guide, logEvent, isShareContentRecipient, shouldShowPrimer]);

	useEffect(() => {
		if (shouldShowPrimer) {
			if (playerMode === 'playing') {
				onPause();
			}
		}
	}, [shouldShowPrimer, playerMode, onPause]);

	useEffect(() => {
		if (!shouldShowPrimer && isShareContentRecipient) {
			setPrimerVisible();
		}
	}, [isShareContentRecipient, setPrimerVisible, shouldShowPrimer]);

	return (
		<Wrapper data-testid="sharePrimer" isVisible={shouldShowPrimer}>
			{activeScreen === 'auth' && <Header isStatic hideMenu hideProfile />}
			<Container activeScreen={activeScreen === 'primer'}>
				<HeaderWrapper>
					<HeaderText size={FontSizes.xl} weight={FontWeights.Medium}>
						{formatMessage(messages.welcome, {
							Calm: (
								<Image
									height="40"
									width={40 * ASPECT_RATIO}
									src={'/_n/images/calm-logo-gradient.svg'}
									alt="Calm"
									aria-label={formatMessage(messages.logo)}
								/>
							),
						})}
					</HeaderText>
				</HeaderWrapper>
				<Text size={FontSizes.base} weight={FontWeights.Demi} data-testid="sharePrimerSenderName">
					{' '}
					{shareSenderFirstName
						? formatMessage(messages.unlockMessage, {
								name: shareSenderFirstName,
						  })
						: formatMessage(messages.unlockMessageNoName)}
				</Text>
				<Description size={FontSizes.base} weight={FontWeights.Regular}>
					{userCanTrial(user) ? formatMessage(messages.upsell) : formatMessage(messages.upsellNoTrial)}
				</Description>
				<ContentDisplay />
				<CTAButton onPress={CTAAction} backgroundColor="buttonBlue" size="lg">
					{formatMessage(messages.continue)}
				</CTAButton>
			</Container>
			<AuthContainer activeScreen={activeScreen === 'auth'}>
				<AuthHeading>
					{authFormMode === 'login'
						? formatMessage(genericMessages.loginTitle)
						: formatMessage(messages.signupHeader)}
				</AuthHeading>
				<AuthForm onAuthSuccess={onAuthSuccess} />
			</AuthContainer>
		</Wrapper>
	);
}

export default SharePrimer;
